/**
 * InformeTest - Componente de generación de informes psicológicos integrales
 * 
 * Este componente ha sido refactorizado para mejorar la mantenibilidad y separar responsabilidades.
 * Utiliza hooks personalizados y componentes modulares para gestionar la funcionalidad.
 * 
 * ACTUALIZACIÓN: Agregada funcionalidad de descarga masiva de reportes
 * 
 * @author Ana Lizcano y Feliciano
 * @version 3.1.0 - Refactorizado con descarga masiva
 */

import MDBox from "components/MDBox";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Context
import { useMaterialUIController } from "context";

// Custom hooks
import { useCandidates } from "./hooks/useCandidates";
import { useReportGeneration } from "./hooks/useReportGeneration";
import { usePDFGenerator } from "./hooks/usePDFGenerator";
import { useNotification } from "./hooks/useNotification";

// Components
import CandidateList from "./components/CandidateList";
import ReportViewer from "./components/ReportViewer";

// Utils
import { NOTIFICATION_TYPES, NOTIFICATION_TITLES, NOTIFICATION_ICONS } from "./utils/constants";

/**
 * Componente principal del informe psicológico integral
 * Renderiza primero una lista de candidatos y luego el informe con datos reales
 */
function InformeTest() {
  // Context
  const [controller] = useMaterialUIController();
  const { searchTerm } = controller;

  // Custom hooks
  const { notification, showNotification, closeNotification } = useNotification();
  const { generatePDF } = usePDFGenerator();
  const {
    paginatedCandidates,
    totalItems,
    filteredCandidates,
    allCandidates,
    currentPage,
    handlePageChange,
    findCandidateById
  } = useCandidates(searchTerm);
  
  const {
    selectedCandidate,
    valuesTestData,
    songTestData,
    drawingTestData,
    isGeneratingReport,
    showReport,
    generateReport,
    resetReport
  } = useReportGeneration();

  // Handlers
  const handleGenerateReport = async (candidateId) => {
    console.log('🚀 Iniciando generación de informe para candidato:', candidateId);
    const candidate = findCandidateById(candidateId);
    await generateReport(candidate, showNotification);
  };

  const handleBackToList = () => {
    resetReport();
  };

  const handleDownloadPDF = (componentRef) => {
    generatePDF(componentRef);
  };

  /**
   * NUEVA FUNCIONALIDAD: Descarga masiva de reportes
   * Genera y descarga múltiples PDFs para los candidatos seleccionados
   */
  const handleBulkDownload = async (selectedCandidateIds) => {
    console.log('📥 Iniciando descarga masiva para:', selectedCandidateIds);
    
    showNotification(
      NOTIFICATION_TYPES.INFO,
      `Preparando descarga de ${selectedCandidateIds.length} reporte(s)...`
    );

    try {
      // TODO: Implementar lógica de descarga masiva
      // Por ahora, mostramos un mensaje de éxito
      
      // Opción 1: Generar todos los PDFs en un solo ZIP
      // Opción 2: Descargar PDFs individuales uno por uno
      // Opción 3: Generar un PDF consolidado con todos los reportes
      
      // Simulación temporal
      setTimeout(() => {
        showNotification(
          NOTIFICATION_TYPES.SUCCESS,
          `Se han preparado ${selectedCandidateIds.length} reporte(s) para descarga`
        );
        
        console.log('✅ Candidatos seleccionados para descarga:', 
          selectedCandidateIds.map(id => {
            const candidate = findCandidateById(id);
            return {
              id: candidate.id_candidato,
              nombre: candidate.nombre_completo,
              documento: candidate.documento
            };
          })
        );
      }, 1500);

    } catch (error) {
      console.error('❌ Error en descarga masiva:', error);
      showNotification(
        NOTIFICATION_TYPES.ERROR,
        'Error al preparar los reportes para descarga'
      );
    }
  };

  // Renderizar vista apropiada
  if (!showReport) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <CandidateList
            paginatedCandidates={paginatedCandidates}
            totalItems={totalItems}
            filteredCandidatesCount={filteredCandidates.length}
            allCandidatesCount={allCandidates.length}
            currentPage={currentPage}
            searchTerm={searchTerm}
            isGeneratingReport={isGeneratingReport}
            onPageChange={handlePageChange}
            onGenerateReport={handleGenerateReport}
            onBulkDownload={handleBulkDownload}
          />
        </MDBox>
        <Footer />

        {/* Notification Snackbar */}
        <MDSnackbar
          color={notification.color}
          icon={NOTIFICATION_ICONS[notification.color]}
          title={NOTIFICATION_TITLES[notification.color]}
          content={notification.message}
          dateTime=""
          open={notification.open}
          onClose={closeNotification}
          close={closeNotification}
          bgWhite
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <ReportViewer
          selectedCandidate={selectedCandidate}
          valuesTestData={valuesTestData}
          songTestData={songTestData}
          drawingTestData={drawingTestData}
          onBackToList={handleBackToList}
          onDownloadPDF={handleDownloadPDF}
        />
      </MDBox>
      <Footer />

      {/* Notification Snackbar */}
      <MDSnackbar
        color={notification.color}
        icon={NOTIFICATION_ICONS[notification.color]}
        title={NOTIFICATION_TITLES[notification.color]}
        content={notification.message}
        dateTime=""
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default InformeTest;